﻿import {translation} from "@2d-front-monorepo/core";
import {Message, PopOver} from "@2d-front-monorepo/design-library";
import {
	Attachments,
	AttachmentStatus,
	createAttachmentHref,
	EntityDocument,
	InstallationByHostData
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useMemo} from "react";
import {Link} from "react-router";
import {API_ENDPOINT} from "../../api-endpoint";
import DocumentControl from "../../components/DocumentControl";
import {DocumentsFilterComponent} from "../../components/Filters/DocumentsFilterComponent";
import {FilteredItemsList} from "../../components/Filters/FilteredItemsList";
import {FilteredItemsProvider} from "../../components/Filters/FilteredItemsProvider";
import MaterialIcon from "../../components/MaterialIcon";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {usePermits} from "../../hooks/usePermits";
import {formatRoute, PrivateRoutes} from "../../routes";

type DocumentControlProps = {
	document: EntityDocument;
	installation: InstallationByHostData;
}

const filesPrefix = API_ENDPOINT.fileDownload;

function getViewHref(document: EntityDocument): string {
	const {entityType, entity} = document;

	switch (entityType) {
		case "holder":
			// PrivateRoutes.holderView ("/holder") is not a registered route (it 404s);
			// the holder's data lives in Settings.
			return PrivateRoutes.settings;

		case "permit":
			return formatRoute(PrivateRoutes.permitView, entity.id);

		case "reservation":
			return formatRoute(PrivateRoutes.reservationView, entity.id);

		case "driver":
			return formatRoute(PrivateRoutes.driverView, entity.id);

		case "residency":
			return formatRoute(PrivateRoutes.residencyView, entity.id);

		case "vehicle":
			return formatRoute(PrivateRoutes.vehicleView, entity.id);
	}
}

function ActionsControl({document}: DocumentControlProps): JSX.Element {
	const {attachment} = document;

	const attachmentDownloadHref = createAttachmentHref(attachment, filesPrefix);
	const editable = attachment.status === AttachmentStatus.Rejected || attachment.status === AttachmentStatus.Expired;

	return (
		<div>
			<PopOver>
				<Link to={getViewHref(document)}>
					<MaterialIcon icon="visibility"/>
					{translation(PermitTranslations.view)}
				</Link>
				<a href={attachmentDownloadHref} target="_blank" download>
					<MaterialIcon icon="download"/>
					{translation(PermitTranslations.download)}
				</a>
				{editable &&
                    <Link to={formatRoute(PrivateRoutes.documentUpdate, attachment.id)}>
                        <MaterialIcon icon="upload_file"/>
						{translation(PermitTranslations.upload)}
                    </Link>
				}
			</PopOver>
		</div>
	);
}

export default function Documents(): JSX.Element {
	const permitsState = usePermits();
	const {installation} = getGlobalContext();
	const documents = useMemo(() => Attachments.list(permitsState), [permitsState]);

	if (documents.length == 0) {
		return (<Message title={{text: translation(PermitTranslations.documents_page__no_documents)}} type="information"/>);
	}

	return (
		<>
			<h1>{translation(PermitTranslations.documents_page__title)}</h1>
			<hr/>
			<FilteredItemsProvider items={documents}>
				<DocumentsFilterComponent/>
				<div role="region" tabIndex={0} className="table--card">
					<table>
						<thead>
						<tr>
							<th>{translation(PermitTranslations.document_type)}</th>
							<th>{translation(PermitTranslations.entity_type)}</th>
							<th className="text">{translation(PermitTranslations.entity_information)}</th>
							<th className="date">{translation(PermitTranslations.expiration_date)}</th>
							<th>{translation(PermitTranslations.document_status)}</th>
							<th className="actions"></th>
						</tr>
						</thead>
						<tbody>
						<FilteredItemsList
							keyExtractor={(document: EntityDocument) => document.attachment.id}
							renderItem={(document: EntityDocument) => (
								<DocumentControl document={document} installation={installation}
								                 actions={<ActionsControl document={document}
								                                          installation={installation}/>}/>
							)}/>
						</tbody>
					</table>
				</div>
			</FilteredItemsProvider>
		</>
	);
}
